import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Stack,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';

import { Card } from '@/imports/ui/chakra/components/Card';
import { REG_EXP } from '@/utilities';

const INVALID_LOGIN_ERROR = 'invalid_email_or_password';
const NO_PASSWORD_SET_ERROR = 'no_password_set';

type ILogInFormValues = {
  email: string;
  password: string;
};

type HandleLoginProps = {
  formAction: string;
};

export const HandleLogin: React.FC<HandleLoginProps> = ({ formAction }) => {
  const { register, formState, handleSubmit } = useForm<ILogInFormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const form = useRef<any>(null);
  const { errors } = formState;
  const router = useRouter();
  // As of now the only kind of error we may get from the server is an invalid username or password error
  const invalidCredsError = router.query.error;

  return (
    <>
      <Heading textAlign="center" mb="8" size="md">
        Log in with Tradewing
      </Heading>
      <Card p="8">
        <Stack
          as="form"
          spacing="4"
          noValidate
          ref={form}
          action={formAction}
          onSubmit={handleSubmit(() => form.current?.submit())}
          method="post"
        >
          {!!invalidCredsError && (
            <Alert status="error">
              <AlertIcon />
              <AlertDescription>
                {invalidCredsError.toString() === NO_PASSWORD_SET_ERROR
                  ? `You don't have a password set. Check your email for an invitation or contact an admin.`
                  : 'There was a problem logging into this account. Try a different username or password.'}
              </AlertDescription>
            </Alert>
          )}

          <FormControl isRequired isInvalid={!!errors.email}>
            <FormLabel requiredIndicator={<></>}>Email</FormLabel>
            <Input
              autoFocus
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: REG_EXP.EmailWithTLD,
                  message: 'Please enter a valid email. E.g. name@domain.com',
                },
              })}
            />
            <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.password}>
            <FormLabel requiredIndicator={<></>}>Password</FormLabel>
            <Input
              type="password"
              {...register('password', {
                required: 'Password is required',
              })}
            />
            <FormErrorMessage>{errors?.password?.message}</FormErrorMessage>
          </FormControl>

          <Button
            w="full"
            isDisabled={formState.isSubmitted && !formState.isValid}
            type="submit"
          >
            Log in
          </Button>
        </Stack>
      </Card>
    </>
  );
};
